import type { Item, Room, Section } from '../db/schema';
import type { Selection } from './useSelection';
import { sectionPath } from './paths';

/**
 * Where you end up after a dialog finishes.
 *
 * All five are about the same thing: the address bar is the app's state, so
 * anything that deletes or moves what the address points at has to move the
 * address too. A plain function rather than a hook — it needs the room, and
 * the room isn't known until after `Board`'s loading guards.
 */
export function boardOutcomes({
  navigate,
  room,
  sectionId,
  itemId,
  path,
  selection,
  close,
}: {
  navigate: (to: string) => void;
  room: Room;
  sectionId: string | undefined;
  /** The item the panel is open on, if any. */
  itemId: string | undefined;
  path: readonly Section[];
  selection: Selection;
  close: () => void;
}) {
  const here = sectionPath(room.id, sectionId);

  return {
    onItemDeleted: (item: Item) => {
      close();
      // Deleting the item the panel is open on would leave it looking at a
      // record that no longer exists.
      if (item.id === itemId) navigate(here);
    },

    onItemsDeleted: () => {
      close();
      if (itemId !== undefined && selection.ids.includes(itemId)) navigate(here);
      selection.clear();
    },

    onItemsMoved: (toRoomId: string) => {
      close();
      selection.clear();
      // Follow them. Watching a tile vanish from the board with no idea where
      // it went is the one thing this action mustn't do — and it also gets the
      // panel off an item that's no longer here.
      navigate(`/room/${toRoomId}`);
    },

    onSectionDeleted: (section: Section) => {
      close();
      // Standing inside the thing that just stopped existing.
      if (section.id === sectionId) navigate(sectionPath(room.id, path.at(-2)?.id));
    },

    onItemImported: (newItemId: string) => {
      close();
      // The import is still running. Opening the panel is what makes its
      // progress visible — several silent seconds read as breakage.
      navigate(`${here}/${newItemId}`);
    },
  };
}
